/**
 * DecisionQueue Component - Phase 5 Epic 5.3
 * Displays pending decisions requiring human approval
 */

import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Clock, AlertTriangle, ArrowUp, Filter } from 'lucide-react';
import { useDecisions, Decision, DecisionType, DecisionPriority } from '@/hooks/useDecisions';
import { Button } from '@/components/ui/Button';
import { cn } from '@/utils/cn';

interface DecisionQueueProps {
  userId?: string;
  className?: string;
  onDecisionClick?: (decision: Decision) => void;
  showFilters?: boolean;
}

export function DecisionQueue({
  userId = 'anonymous',
  className,
  onDecisionClick,
  showFilters = true,
}: DecisionQueueProps) {
  const {
    decisions,
    pendingCount,
    isLoading,
    error,
    clearError,
  } = useDecisions({ userId, autoFetch: true, status: 'pending' });

  const [filterType, setFilterType] = useState<DecisionType | 'all'>('all');
  const [filterPriority, setFilterPriority] = useState<DecisionPriority | 'all'>('all');

  // Apply filters
  const filteredDecisions = decisions.filter((decision) => {
    if (filterType !== 'all' && decision.type !== filterType) return false;
    if (filterPriority !== 'all' && decision.priority !== filterPriority) return false;
    return true;
  });

  // Sort by priority then date
  const sortedDecisions = [...filteredDecisions].sort((a, b) => {
    const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const priorityDiff = priorityOrder[a.priority] - priorityOrder[b.priority];
    if (priorityDiff !== 0) return priorityDiff;
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });

  return (
    <div className={cn('w-full', className)}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-orange-500/20 border border-orange-400/30 rounded-lg">
            <AlertCircle className="h-6 w-6 text-orange-400" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-white">Decision Queue</h2>
            <p className="text-sm text-white/70">
              {pendingCount} {pendingCount === 1 ? 'decision' : 'decisions'} awaiting approval
            </p>
          </div>
        </div>

        {showFilters && (
          <div className="flex items-center gap-2">
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value as any)}
              className="px-3 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
            >
              <option value="all" className="bg-slate-800">All Priorities</option>
              <option value="critical" className="bg-slate-800">Critical</option>
              <option value="high" className="bg-slate-800">High</option>
              <option value="medium" className="bg-slate-800">Medium</option>
              <option value="low" className="bg-slate-800">Low</option>
            </select>

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value as any)}
              className="px-3 py-2 bg-white/[0.04] border border-white/[0.06] rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
            >
              <option value="all" className="bg-slate-800">All Types</option>
              <option value="budget_reallocation" className="bg-slate-800">Budget Reallocation</option>
              <option value="campaign_launch" className="bg-slate-800">Campaign Launch</option>
              <option value="performance_alert" className="bg-slate-800">Performance Alert</option>
              <option value="agent_action" className="bg-slate-800">Agent Action</option>
              <option value="ml_override" className="bg-slate-800">ML Override</option>
              <option value="content_approval" className="bg-slate-800">Content Approval</option>
              <option value="spend_threshold" className="bg-slate-800">Spend Threshold</option>
            </select>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-4 p-4 bg-red-500/20 border border-red-400/30 rounded-lg flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-300 flex-shrink-0" />
          <p className="text-sm text-red-200 flex-1">{error}</p>
          <button
            onClick={clearError}
            className="text-red-300 hover:text-red-100 text-sm font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-white/[0.06] border-t-primary-600"></div>
          <p className="mt-4 text-white/70">Loading decisions...</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && sortedDecisions.length === 0 && (
        <div className="text-center py-12 bg-white/[0.03] rounded-lg border-2 border-dashed border-white/[0.06]">
          <CheckCircle className="h-12 w-12 text-green-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">All Caught Up!</h3>
          <p className="text-white/70">
            No pending decisions require your approval right now.
          </p>
        </div>
      )}

      {/* Decision List */}
      {!isLoading && sortedDecisions.length > 0 && (
        <div className="space-y-4">
          {sortedDecisions.map((decision) => (
            <DecisionCard
              key={decision.id}
              decision={decision}
              onClick={() => onDecisionClick?.(decision)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Decision Card Component
interface DecisionCardProps {
  decision: Decision;
  onClick?: () => void;
}

function DecisionCard({ decision, onClick }: DecisionCardProps) {
  const priorityStyles = {
    critical: 'bg-red-500/20 border-red-400/30 text-red-300',
    high: 'bg-orange-500/20 border-orange-400/30 text-orange-300',
    medium: 'bg-blue-500/20 border-blue-400/30 text-blue-300',
    low: 'bg-gray-500/20 border-gray-400/30 text-gray-300',
  };

  const priorityIcons = {
    critical: AlertCircle,
    high: AlertTriangle,
    medium: Clock,
    low: Clock,
  };

  const typeLabels: Record<DecisionType, string> = {
    budget_reallocation: 'Budget Reallocation',
    campaign_launch: 'Campaign Launch',
    performance_alert: 'Performance Alert',
    agent_action: 'Agent Action',
    ml_override: 'ML Override',
    content_approval: 'Content Approval',
    spend_threshold: 'Spend Threshold',
  };

  const PriorityIcon = priorityIcons[decision.priority];

  const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const expiresIn = (dateString?: string) => {
    if (!dateString) return null;
    const expires = new Date(dateString);
    const now = new Date();
    const hours = Math.floor((expires.getTime() - now.getTime()) / (1000 * 60 * 60));

    if (hours < 0) return 'Expired';
    if (hours < 1) return 'Expires soon';
    if (hours < 24) return `Expires in ${hours}h`;
    return `Expires in ${Math.floor(hours / 24)}d`;
  };

  const expiryInfo = expiresIn(decision.expiresAt);
  const isUrgent = expiryInfo && (expiryInfo === 'Expired' || expiryInfo === 'Expires soon');

  return (
    <div
      onClick={onClick}
      className={cn(
        'bg-white/[0.04] rounded-lg border-l-4 shadow-md hover:shadow-lg transition-all cursor-pointer p-6 hover:scale-[1.01]',
        priorityStyles[decision.priority].split(' ')[1] // Get border color
      )}
    >
      <div className="flex items-start justify-between gap-4">
        {/* Left: Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center gap-3 mb-3">
            <span
              className={cn(
                'px-3 py-1 rounded-full text-xs font-bold uppercase flex items-center gap-1.5',
                priorityStyles[decision.priority]
              )}
            >
              <PriorityIcon className="h-3.5 w-3.5" />
              {decision.priority}
            </span>

            <span className="px-3 py-1 bg-white/[0.03] rounded-full text-xs font-medium text-white/80">
              {typeLabels[decision.type]}
            </span>

            {decision.requiredApprovers > 1 && (
              <span className="px-3 py-1 bg-purple-500/20 border border-purple-400/30 rounded-full text-xs font-medium text-purple-300">
                {decision.currentApprovers}/{decision.requiredApprovers} Approvals
              </span>
            )}
          </div>

          {/* Title & Description */}
          <h3 className="text-lg font-bold text-white mb-2">{decision.title}</h3>
          <p className="text-sm text-white/80 mb-3">{decision.description}</p>

          {/* AI Reasoning */}
          {decision.aiReasoning && (
            <div className="bg-blue-500/10 border border-blue-400/30 rounded-md p-3 mb-3">
              <p className="text-xs font-semibold text-blue-300 mb-1">AI Analysis:</p>
              <p className="text-sm text-blue-200">{decision.aiReasoning}</p>
            </div>
          )}

          {/* Suggested Action */}
          {decision.suggestedAction && (
            <div className="bg-green-500/10 border border-green-400/30 rounded-md p-3 mb-3">
              <p className="text-xs font-semibold text-green-300 mb-1">
                Suggested Action:
              </p>
              <p className="text-sm text-green-200">{decision.suggestedAction}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-4 text-xs text-white/60">
            <span className="flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {timeAgo(decision.createdAt)}
            </span>

            {expiryInfo && (
              <span className={cn(
                'flex items-center gap-1 font-medium',
                isUrgent ? 'text-red-600' : 'text-white/70'
              )}>
                <AlertCircle className="h-3.5 w-3.5" />
                {expiryInfo}
              </span>
            )}

            {decision.entityType && (
              <span className="capitalize">
                {decision.entityType}: {decision.entityId?.substring(0, 8)}...
              </span>
            )}
          </div>
        </div>

        {/* Right: Quick Actions */}
        <div className="flex flex-col gap-2">
          <Button
            variant="default"
            size="sm"
            className="whitespace-nowrap"
          >
            Review
          </Button>

          {decision.escalated && (
            <div className="flex items-center gap-1 text-xs text-orange-600 font-medium">
              <ArrowUp className="h-3.5 w-3.5" />
              Escalated
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact Decision Badge (for header)
interface DecisionBadgeProps {
  userId?: string;
  onClick?: () => void;
  className?: string;
}

export function DecisionBadge({
  userId = 'anonymous',
  onClick,
  className,
}: DecisionBadgeProps) {
  const { pendingCount } = useDecisions({ userId, autoFetch: true });

  if (pendingCount === 0) return null;

  return (
    <button
      className={cn('relative', className)}
      onClick={onClick}
      aria-label={`${pendingCount} pending decisions`}
    >
      <AlertCircle className="h-5 w-5 text-orange-600" />
      <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-orange-500 text-[10px] font-bold text-white flex items-center justify-center">
        {pendingCount > 9 ? '9+' : pendingCount}
      </span>
    </button>
  );
}
